import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import EnterpriseInquiry from '../components/EnterpriseInquiry';
import {
  KIT_TIERS, PILOT, PLATFORM_TIERS, PLATFORM_METERS, KIT_METERS, TEAM_ATTACH, DEV_RUN_USD, appKit, kitBuild, kitBusiness, platformFree, platformStart, platformTeam, platformBusiness, dollars, perMonth,
} from '../data/plans';
import { MODULES, moduleHref } from '../data/modules';
import { PlanRow } from '../components/module';
import PageWorld from '../components/site/PageWorld';
import { PRICING } from '../data/worlds';
import { PageFilm } from '../components/PageFilm';
import PitchButton from '../components/PitchButton';

// Public pricing. Cards and dollars come from data/plans.js.

function TierGrid({ tiers }) {
  return (
    <div className="cs-land-price-grid">
      {tiers.map((t) => (
        <div key={t.name} className={`cs-sw-card ${t.featured ? 'cs-land-featured' : ''}`}>
          <h3>{t.name}</h3>
          <div className="cs-sw-price">{t.price}</div>
          <ul className="cs-product-specs">
            {t.bundle.map((b) => (
              <li key={b}>{b}</li>
            ))}
          </ul>
          <small>{t.who}</small>
          {t.href && (
            <div className="cs-land-cta-row" style={{ marginTop: 12 }}>
              <Link to={t.href} className={`cs-btn cs-btn-sm ${t.featured ? 'cs-btn-primary' : 'cs-btn-outline'}`}>
                {t.cta}
              </Link>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function MeterTable({ rows }) {
  return (
    <div className="cs-plan-tablewrap cs-land-compare">
      <table>
        <thead>
          <tr>
            <th>Meter</th>
            <th>Rate</th>
            <th>Note</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(([meter, rate, note]) => (
            <tr key={meter}>
              <td><strong>{meter}</strong></td>
              <td className="cs-land-us">{rate}</td>
              <td>{note}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function Pricing() {
  const { user } = useAuth();
  const member = !!user && !user.isAnonymous;
  return (
    <PageWorld world={PRICING}>
    <div className="cs-store" data-nova-note="Pricing: Platform (Free, Start, Team, Business) and App Kit (Build, Kit, Kit Business) tiers with the compare table; every Buy button starts Stripe checkout.">
      {member && (
        <p className="cs-store-note" style={{ textAlign: 'center', marginTop: 12 }}>
          Team review: <Link to="/pricing-internal">full internal spec →</Link>
        </p>
      )}
      <header className="cs-store-hero">
        <h1>Platform or App Kit</h1>
        <p>
          Already have ERP, CRM, or tickets? Buy Platform.
          Need an app your people log into? Rent App Kit.
          Need both? Listed bundles — not a silent upgrade.
        </p>
        <div className="cs-land-cta-row cs-land-cta-center">
          <a href="#platform" className="cs-btn cs-btn-primary">
            Already have the systems
          </a>
          <a href="#kit" className="cs-btn cs-btn-outline">
            Need a new app
          </a>
        </div>
      </header>

      <PageFilm
        base="/media/pricing-film"
        prefix="pricing"
        title="Platform or App Kit"
        summary="Two products. Both together is a written bundle — Kit and Platform Team, fifteen percent off the sum — not a hidden merge."
      />
      <PitchButton focus="pricing" label="Ask NoVA to walk the plans" hint="Interrupt with your own numbers." />

      <section className="cs-store-section">
        <div className="cs-land-two-products">
          <div className="cs-sw-card">
            <h3>Platform — nQube + seats + agentOS</h3>
            <p>
              You already have ERP / CRM / tickets / APIs. Done is a gated
              pipeline that writes into those systems.
            </p>
            <small>Priced against workflow tools.</small>
          </div>
          <div className="cs-sw-card">
            <h3>App Kit — instance + layer + Dev Console</h3>
            <p>
              You do not have the app you need. Done is a running instance
              people open — the public site first, then sign-in and the books
              on that same URL. The rent unlocks Dev Console: you change the
              app and ship it, on a foundation already built for the load,
              watched on every deploy, and updated with the rent. The fee
              is the license for that foundation.
            </p>
            <small>Priced against internal-app platforms.</small>
          </div>
        </div>
      </section>

      <section className="cs-store-section" id="platform">
        <h2>Platform</h2>
        <p className="cs-store-note" style={{ maxWidth: 860 }}>
          Shared nQube and production connectors. Start is {dollars(platformStart)} for 3,000
          workflow runs. Team is {dollars(platformTeam)} for 12,000. Business is {dollars(platformBusiness)}. None of
          those include a dedicated instance. Pilot is delivery of your first
          gated pipeline on a real system, $9,000–$24,000, not a plan row
          and not a first custom app.
        </p>
        <TierGrid tiers={PLATFORM_TIERS} />
        <p className="cs-land-micro" style={{ marginTop: 14 }}>
          Viewers stay free. Start, Team, and Business are priced in workflow
          runs. A run with one gated commit bills 4 Runtime Credits internally
          (run 2 + commit 2). Enterprise is quoted in those credits.
        </p>
        <div className="cs-land-cta-row">
          <Link to={platformStart.href} className="cs-btn cs-btn-primary">
            {platformStart.cta}
          </Link>
          <Link to={platformFree.href} className="cs-btn cs-btn-outline">
            {platformFree.cta}
          </Link>
          <a href={PILOT.href} className="cs-btn cs-btn-outline">
            {PILOT.label}
          </a>
          <a href="#enterprise" className="cs-btn cs-btn-outline">
            Enterprise review
          </a>
        </div>
      </section>

      <section className="cs-store-section" id="kit">
        <h2>App Kit</h2>
        <p className="cs-store-note" style={{ maxWidth: 860 }}>
          Kit at {perMonth(appKit)} is the instance: a URL, a database, and a layer
          repo, with Dev Console. Kit Site at {perMonth(kitBuild)} is a public site on a
          shared host and does not include that console. The monthly fee is
          the license for the foundation. A managed host is included. Your
          cloud or on-prem keeps the license and drops the host meters.
          nQube on that instance only is included. Service health and HTTP
          feature checks come with Kit. Browser feature checks and the
          monthly passive security baseline are the Assurance add-on — talk
          to us. Active security tests stay a confirmed nventr run.
          Org-wide pipelines into systems outside the app still belong on
          Platform. Kit Business is {dollars(kitBusiness)}. Viewers on an instance are
          unlimited.{' '}
          <Link to="/app-kit#enterprise-support" className="cs-land-textlink" id="kit-enterprise">
            Kit Enterprise named support
          </Link>{' '}
          includes operator-led Dev Console while your users stay on production.
        </p>
        <TierGrid tiers={KIT_TIERS} />
        <div className="cs-land-metered" style={{ marginTop: 14 }} id="native">
          <strong>Native app bundles (Kit and Kit Business add-ons).</strong>{' '}
          Desktop at $49/mo builds your app as macOS (.dmg) and Windows
          (.msi / installer) apps. Mobile at $79/mo builds iOS (.ipa for
          TestFlight and the App Store) and Android (.aab for Play). Same
          sign-in, same data, same release: the instance's web app wrapped in
          real native shells, built on hosted runners from App Builder,
          signed with your own certificates when you upload them. Store
          accounts and submissions stay yours; 20 builds a month per bundle.
        </div>
        <div className="cs-land-metered" style={{ marginTop: 14 }} id="lab">
          <strong>Lab (organization add-on, any plan with App Builder).</strong>{' '}
          $79/mo adds a studio, research, refine and test agent to every App
          Builder app in the organization. Talk an idea through with NoVA by
          voice: she interviews you, browses the web in a browser you watch
          live, and drafts a visual concept — problem, people, landscape,
          features, wireframed screens, flows — that you revise and accept
          before a line is built. Research searches the web server-side and
          files a draft brief with sources and proposals; Refine turns it
          into changes you apply with one click; Test drives your hosted app
          or its UAT twin in a real browser from a plain-language scenario,
          one screenshot per step, filed as a QA run. Steps that would change
          data on the live app wait for an admin's approval. Performance
          runs load your pages in the same browser and time your agent
          against budgets. Includes 30 research runs, 120 browser minutes
          and 20 concept images a month (concept drafts count as research
          runs; live browsing and performance draw on the same minutes);
          then $0.50 per research run, $0.10 per browser minute and $0.25
          per concept image.
        </div>
        <div className="cs-land-metered" style={{ marginTop: 14 }} id="marketing">
          <strong>Marketing (organization add-on, any paid plan).</strong>{' '}
          $99/mo gives your organization its own tenant on nventr Marketing:
          brand knowledge, a strategy matrix and content plans; generated
          posts — copy, images, video — that you review and approve before
          anything is published to your own TikTok, Instagram, Facebook, X,
          LinkedIn and YouTube accounts; every comment, mention and metric
          back as encounters and insights. Same account and the agreements
          you already accepted — admins arrive as admins, members as members
          — and you can disconnect a channel and delete what it pulled at any
          time. Hosted model tokens, images and video seconds are metered to
          your organization at the rates above; your own keys zero that line.
        </div>
        <p className="cs-land-micro" style={{ marginTop: 14 }}>
          How the rented kit, layer repo, and 12-hour license work:{' '}
          <Link to="/app-kit">App Kit explainer →</Link>
        </p>
        <div className="cs-land-cta-row">
          <Link to={appKit.href} className="cs-btn cs-btn-primary">
            {appKit.cta}
          </Link>
          <a href="#enterprise" className="cs-btn cs-btn-outline">
            Enterprise review
          </a>
        </div>
      </section>

      <section className="cs-store-section" id="examples">
        <h2>What a month costs — worked examples</h2>
        <p className="cs-store-note" style={{ maxWidth: 860 }}>
          Every figure below is the plan fee plus the meters on this page.
          Synthetic volumes; the arithmetic is the point. Bring your own
          model key and the hosted-token line is $0 in all of them.
        </p>
        <div className="cs-land-two-products">
          <div>
            <h3>Platform</h3>
            <div className="mdp-cost">
              <div className="cs-sw-card mdp-cost-row">
                <h3>One builder, 2,500 invoices a month</h3>
                <p className="mdp-cost-math">{platformStart.name} {dollars(platformStart)} includes 3,000 workflow runs. 2,500 gated invoice runs stay inside the allowance.</p>
                <div className="mdp-cost-result">{perMonth(platformStart)}</div>
              </div>
              <div className="cs-sw-card mdp-cost-row">
                <h3>Three builders, 10,000 runs</h3>
                <p className="mdp-cost-math">{platformTeam.name} {dollars(platformTeam)} includes 3 builders and 12,000 workflow runs. Viewers are free.</p>
                <div className="mdp-cost-result">{perMonth(platformTeam)}</div>
              </div>
              <div className="cs-sw-card mdp-cost-row">
                <h3>Start, 400 runs over</h3>
                <p className="mdp-cost-math">3,400 runs on {platformStart.name}: 400 past the allowance × 2 Runtime Credits × $0.015 at the top of the range = $12.</p>
                <div className="mdp-cost-result">$27/mo</div>
              </div>
            </div>
          </div>
          <div>
            <h3>App Kit</h3>
            <div className="mdp-cost">
              <div className="cs-sw-card mdp-cost-row">
                <h3>Solo founder, a change or two a week</h3>
                <p className="mdp-cost-math">{appKit.name} {dollars(appKit)} includes {appKit.included.devRuns} Dev Console runs and the managed host. Eight sessions: nothing past the fee.</p>
                <div className="mdp-cost-result">{perMonth(appKit)}</div>
              </div>
              <div className="cs-sw-card mdp-cost-row">
                <h3>Small team, three changes a day</h3>
                <p className="mdp-cost-math">{kitBusiness.name} {dollars(kitBusiness)} includes {kitBusiness.included.devRuns} runs. Sixty sessions: {60 - kitBusiness.included.devRuns} × ${DEV_RUN_USD.toFixed(2)} past the allowance.</p>
                <div className="mdp-cost-result">${349 + (60 - kitBusiness.included.devRuns) * DEV_RUN_USD}/mo</div>
              </div>
              <div className="cs-sw-card mdp-cost-row">
                <h3>Kit in your own cloud</h3>
                <p className="mdp-cost-math">{appKit.name} {dollars(appKit)} is the license; compute, storage, egress and GPU on our side drop off the bill when you host.</p>
                <div className="mdp-cost-result">{perMonth(appKit)}</div>
              </div>
            </div>
          </div>
        </div>
        <div className="cs-land-metered" style={{ marginTop: 14 }} id="dev-console-run">
          <strong>What a Dev Console run is.</strong>{' '}
          One agent session. The worker, judge and routine sessions a graph or
          a routine opens each count as a run; verifier passes ride along with
          the session they verify. {appKit.name} includes {appKit.included.devRuns} a
          month, {kitBusiness.name} includes {kitBusiness.included.devRuns}; past
          that a run is ${DEV_RUN_USD.toFixed(2)}, whichever model it ran on.
          A run is a run in every mode — a new app on the kit, an app re-seated
          on it, or an app you keep as it is — and the instance fee is the same
          whether the primary repo is a layer or your own code.
          {' '}<Link to="/dev-console#cost">Worked on the Dev Console page →</Link>
          {' · '}<Link to="/dev-console#modes">The three modes →</Link>
        </div>
      </section>

      <section className="cs-store-section" id="where">
        <h2>Where each module is</h2>
        <p className="cs-store-note" style={{ maxWidth: 860 }}>
          Read from the same record each module page shows under “Where you get it”, so the two cannot drift.
        </p>
        <div className="cs-land-modules cs-plan-where">
          {MODULES.filter((m) => m.plans && !(m.route && m.route !== `/platform/${m.slug}`)).map((m) => (
            <div key={m.slug} className="cs-sw-card">
              <h3 style={{ marginTop: 0 }}><Link to={moduleHref(m.slug)} className="cs-land-textlink">{m.name}</Link></h3>
              <PlanRow plans={m.plans} />
            </div>
          ))}
        </div>
      </section>

      <section className="cs-store-section" id="bundle">
        <h2>Both together — a written bundle, not a hidden merge-up</h2>
        <div className="cs-land-metered">
          <strong>Team attach: Kit {dollars(appKit)} + Platform Team {dollars(platformTeam)} → {TEAM_ATTACH.price}</strong>
          {' '}({TEAM_ATTACH.note}). You get the instance and org-wide nQube.
        </div>
        <div className="cs-land-metered" style={{ marginTop: 12 }}>
          <strong>Department attach: Kit Business $349 + Platform Business $599 → $825 / mo</strong>
          {' '}(13% off $948). Same rule, grown-up SKU.
        </div>
        <p className="cs-land-micro">
          Buying Kit alone does not unlock Platform for the org.
          Buying Platform alone does not give you a URL, database, or Dev
          Console. Platform is seats, credits, and optional VPC nodes.
          Kit is instance rent, the license, and Dev Console runs.
        </p>
        <div className="cs-land-cta-row">
          <a href="#enterprise" className="cs-btn cs-btn-primary">
            Tell us about a bundle
          </a>
        </div>
      </section>

      <section className="cs-store-section" id="enterprise">
        <h2>Enterprise</h2>
        <p className="cs-store-note" style={{ maxWidth: 860 }}>
          Platform Enterprise is typical year-1 from $8,000/mo. Kit Enterprise
          is from $3,000/mo. Neither is a self-serve checkout. Send a brief
          and we’ll spend 30 minutes on the first write.
        </p>
        <div className="cs-land-metered" style={{ marginTop: 14, maxWidth: 860 }}>
          <strong>Kit Enterprise — when users are already on the app.</strong>{' '}
          For teams with people live on a production instance — contract tools,
          ops apps, vertical workflows — named support includes nventr operators
          running Dev Console on your layer repo while the app stays up: feedback
          becomes a reviewed diff, a verifier pass on preview, ship to UAT, and
          production when your gate allows. Stuck queue or worker jobs are
          unblocked the same way — fix, deploy, verify — not a ticket on someone
          else’s roadmap. Self-serve Kit and Kit Business include Dev Console runs
          you operate; Enterprise adds the people and SLA. This is not Platform
          Pilot (your first gated pipeline into ERP or CRM — $9,000–$24,000 at{' '}
          <a href={PILOT.href} className="cs-land-textlink">{PILOT.label.toLowerCase()}</a>).
          {' '}<Link to="/app-kit#enterprise-support" className="cs-land-textlink">How live production support works →</Link>
          {' · '}<Link to="/dev-console" className="cs-land-textlink">Dev Console →</Link>
        </div>
        <EnterpriseInquiry />
      </section>

      <section className="cs-store-section">
        <h2>Meters — split per product</h2>
        <div className="cs-land-two-products">
          <div>
            <h3>Platform</h3>
            <MeterTable rows={PLATFORM_METERS} />
          </div>
          <div>
            <h3>App Kit</h3>
            <MeterTable rows={KIT_METERS} />
          </div>
        </div>
        <p className="cs-land-micro">
          Bring your own model and the hosted-token line is $0. Seats and
          instance rent stay. Hosted model credits never stop your app: at a
          $0 balance hosted model calls pause with a clear 402 until you top
          up; everything else keeps running. Build and Run wallets stay separate so a week
          of coding agents cannot empty production, and a month of invoice
          intake cannot freeze the Dev Console.
        </p>
      </section>

      <footer className="cs-store-footer">
        Consumer appliance plans and Edge OS live with the hardware they
        attach to — the{' '}
        <Link to="/store">store</Link> and{' '}
        <Link to="/partners">partners</Link>. Hardware is not a third
        platform SKU.
      </footer>
    </div>
    </PageWorld>
  );
}
